import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate } from 'react-router';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { ConsultationStatus, RecordingMode } from '@/shared/api/transcriptor';
import { PATHS, toastErrors } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Button, Popover, toastCaller } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import {
  canReorderConsultation,
  getTranscriptorErrorMessage,
  transcriptorModel,
  useRemoveConsultation,
} from '@/entities/transcriptor';

import { i18n } from '../../config';
import { TransferModal } from '../TransferModal';

import styles from './ConsultationOptionsPopover.module.scss';

type ConsultationOptionsPopoverProps = {
  consultationId: string;
  patientId: string;
  className?: string;
  isArchived?: boolean;
  canArchive: boolean;
  canRestore: boolean;
  canDelete: boolean;
  status?: ConsultationStatus;
  recordingMode?: RecordingMode;
  error?: string | null;
};

export const ConsultationOptionsPopover: FC<ConsultationOptionsPopoverProps> = (
  props,
) => {
  const {
    consultationId,
    patientId,
    className,
    isArchived,
    canArchive,
    canRestore,
    canDelete,
    status,
    recordingMode,
    error,
  } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const removeConsultation = useRemoveConsultation();
  const isArchiveEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_ArchiveReports,
    ),
  );

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  const notifyError = (message: string) => {
    toastCaller({
      type: 'error',
      heading: formatMessage(toastErrors.errorHeading),
      message,
    });
  };

  const handleArchive = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsPopoverOpen(false);

    try {
      await dispatch(
        transcriptorModel.thunks.archiveConsultation(consultationId),
      ).unwrap();
    } catch {
      notifyError(formatMessage(i18n.consultationOptionsMessages.archiveError));
    }
  };

  const handleDelete = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsPopoverOpen(false);

    // Resolves on confirmation, before the request is sent; a failure still
    // shows its notification after the page is left.
    if (await removeConsultation(consultationId)) {
      navigate(generatePath(PATHS.patientRecordings, { patientID: patientId }));
    }
  };

  const handleRestore = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsPopoverOpen(false);

    try {
      await dispatch(
        transcriptorModel.thunks.restoreConsultation(consultationId),
      ).unwrap();
    } catch {
      notifyError(formatMessage(i18n.consultationOptionsMessages.restoreError));
    }
  };

  const handleReorder = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsPopoverOpen(false);

    trackEvent({
      eventName: 'TranscriptorReportPageReorderButtonClicked',
    });

    try {
      await dispatch(
        transcriptorModel.thunks.reprocessConsultation({ consultationId }),
      ).unwrap();
    } catch (error) {
      const refusal = getTranscriptorErrorMessage(error);

      notifyError(
        formatMessage(refusal ?? i18n.consultationOptionsMessages.reorderError),
      );
    }
  };

  const handleTransfer = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsPopoverOpen(false);
    setIsTransferModalOpen(true);
  };

  return (
    <>
      <Popover
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        className={className}
        popoverClassName={styles.popover}
        closeOnMouseLeave={false}
      >
        {canReorderConsultation({ status, error, recordingMode }) && (
          <Button
            onClick={handleReorder}
            variant="tertiary"
            size="small"
            icon="rotate"
            className={styles.popoverButton}
          >
            {formatMessage(i18n.consultationOptionsMessages.reorderButton)}
          </Button>
        )}

        <Button
          onClick={handleTransfer}
          variant="tertiary"
          size="small"
          icon="share"
          className={styles.popoverButton}
        >
          {formatMessage(i18n.consultationOptionsMessages.transferButton)}
        </Button>

        {isArchived && canRestore && (
          <Button
            onClick={handleRestore}
            variant="tertiary"
            size="small"
            icon="turn"
            className={styles.popoverButton}
          >
            {formatMessage(i18n.consultationOptionsMessages.restoreButton)}
          </Button>
        )}

        {!isArchived && isArchiveEnabled && canArchive && (
          <Button
            onClick={handleArchive}
            variant="tertiary"
            size="small"
            danger
            icon="archive"
            className={styles.popoverButton}
          >
            {formatMessage(i18n.consultationOptionsMessages.archiveButton)}
          </Button>
        )}

        {(isArchived || !isArchiveEnabled) && canDelete && (
          <Button
            onClick={handleDelete}
            variant="tertiary"
            size="small"
            danger
            icon="delete"
            className={styles.popoverButton}
          >
            {formatMessage(i18n.consultationOptionsMessages.deleteButton)}
          </Button>
        )}
      </Popover>

      <TransferModal
        isOpen={isTransferModalOpen}
        consultationId={consultationId}
        patientId={patientId}
        onClose={() => setIsTransferModalOpen(false)}
      />
    </>
  );
};
